import { GQL } from '@aline/graphql';
import { omit } from 'ramda';
import type { Descendant, NodeEntry, Path } from 'slate';
import { Editor, Element, Node, Text, Transforms } from 'slate';

import { normalizeCheckboxElement } from './checkbox-normalizations';
import { constructTextNode } from './paragraph-normalizations';
import { mergeWithDefaults } from '../../../util';
import type {
  AlineInlineElement,
  BreakElement,
  CheckboxElement,
  CheckboxOptionFieldElement,
  DrawingElement,
  InlineInstructionFieldElement,
  InputFieldElement,
  LinkElement,
  TabElement,
} from '../../internal';
import {
  cannedFieldType,
  checkboxType,
  createCheckboxElement,
  createCheckboxOptionFieldElement,
  createDefaultBreak,
  createDefaultDrawing,
  createDefaultInlineInstructionField,
  createDefaultLink,
  createDefaultNonSigField,
  drawingType,
  dynamicInlineType,
  inputFieldType,
  instructionFieldElementType,
  isAlineInlineElement,
  isBookmarkMarkerElement,
  isCannedFieldElement,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isDrawingElement,
  isInlineInstructionFieldElement,
  isLinkElement,
  isPageBreakElement,
  isSignatureFieldElement,
  isSuggestionMarkerElement,
  isTabElement,
  Marks,
  propsEq,
  Queries,
  tabType,
} from '../../internal';
import { bookmarkEndType, bookmarkStartType } from '../../types/elements/BookmarkMarkerElement';
import {
  suggestionMarkerEndType,
  suggestionMarkerStartType,
} from '../../types/elements/SuggestionMarkerElement';

export function normalizeInline(editor: Editor, inlinePath: Path): void {
  if (!Node.has(editor, inlinePath)) return;

  const inline = Node.get(editor, inlinePath);
  if (!isAlineInlineElement(inline)) return;

  if (
    Queries.nodeIsEmpty(inline) &&
    !isPageBreakElement(inline) &&
    !isSignatureFieldElement(inline) &&
    !isCannedFieldElement(inline) &&
    !isCheckboxElement(inline) &&
    !isCheckboxOptionFieldElement(inline) &&
    !isDrawingElement(inline) &&
    !isTabElement(inline) &&
    !isBookmarkMarkerElement(inline) &&
    !isSuggestionMarkerElement(inline) &&
    !isInlineInstructionFieldElement(inline)
  ) {
    Transforms.removeNodes(editor, { at: inlinePath, voids: true });
    return;
  }

  /**
   * It is important to first validate the inline props, and then check again that the node is still an inline after.
   * Inside validateInlineProps, we may unwrap/remove the inline if we cannot validate it.
   */
  const possibleInline = validateInlineProps(editor, [inline, inlinePath]);
  if (!Element.isElement(possibleInline) || !Editor.isInline(editor, possibleInline)) return;

  if (
    isCheckboxElement(possibleInline) &&
    normalizeCheckboxElement(editor, [possibleInline, inlinePath])
  ) {
    return;
  }

  const inlineAllowsNestedTab =
    isLinkElement(possibleInline) || isCheckboxOptionFieldElement(possibleInline);

  for (let i = 0, n = 0; i < possibleInline.children.length; i++, n++) {
    let childNode: Descendant = possibleInline.children[i];
    const isFirst = i === 0;
    const isLast = i === possibleInline.children.length - 1;

    /**
     * Inlines should not contain other block-level nodes or inlines.  Unwrap all nested Elements (including inlines), if necessary, and only allow text children.
     */
    while (
      Element.isElement(childNode) &&
      !isCheckboxElement(childNode) &&
      // tab can be inside link. Ex. TOC
      !(isTabElement(childNode) && inlineAllowsNestedTab)
    ) {
      Transforms.unwrapNodes(editor, { at: inlinePath.concat(n), voids: true });

      const node = Node.get(editor, inlinePath.concat(n));
      if (!Editor.isEditor(node)) childNode = node;
    }

    if (
      Element.isElement(childNode) &&
      Editor.isInline(editor, childNode) &&
      (isCheckboxElement(childNode) || (isTabElement(childNode) && inlineAllowsNestedTab))
    ) {
      const inlineTextEntries = Node.texts(childNode);
      const firstInlineTextEntry = inlineTextEntries.next().value;
      const firstInlineText = firstInlineTextEntry?.[0];
      const textStyles = firstInlineText
        ? omit(['text', 'comments', 'suggestedEdits'], firstInlineText)
        : Marks.default;
      const prevNodeForPadding = n > 0 ? Node.getIf(editor, inlinePath.concat(n - 1)) : undefined;

      if (
        !prevNodeForPadding ||
        (Element.isElement(prevNodeForPadding) && Editor.isInline(editor, prevNodeForPadding))
      ) {
        const textNode = constructTextNode(editor, inlinePath, textStyles, n);
        Transforms.insertNodes(editor, textNode, {
          at: inlinePath.concat(n),
        });
        return;
      }

      if (isLast) {
        const textNode = constructTextNode(editor, inlinePath, textStyles, n);
        Transforms.insertNodes(editor, textNode, {
          at: inlinePath.concat(n + 1),
          voids: true,
        });
        return;
      }

      continue;
    }

    // There is no prev node so we simply continue.
    if (n <= 0) continue;

    /**
     * Get the prev node here as it may have changed during the course of normalizing the inline.
     * For instance, there are adjacent text nodes inside the inline and we previously merged them,
     * we need to make sure we get the "up to date" merged prev node here.
     */
    const prevNode = Node.get(editor, inlinePath.concat(n - 1));

    // Merge adjacent text nodes inside an inline that match.
    if (prevNode && Text.isText(childNode) && Text.isText(prevNode)) {
      if (propsEq(childNode, prevNode)) {
        const { text, ...properties } = prevNode;

        // Use editor.apply instead of AlineTransforms.mergeNodes as applying the op directly has performance benefits (particularly for changes to large docs).
        editor.apply({
          path: inlinePath.concat(n),
          position: text.length,
          properties,
          type: 'merge_node',
        });

        n--;
      } else if (prevNode.text === '') {
        Transforms.removeNodes(editor, { at: inlinePath.concat(n - 1) });
        n--;
      } else if (!isFirst && isLast && childNode.text === '') {
        const prevIsNestedVoidInline =
          Element.isElement(prevNode) &&
          Editor.isInline(editor, prevNode) &&
          (isCheckboxElement(prevNode) || isTabElement(prevNode));
        if (!prevIsNestedVoidInline) {
          Transforms.removeNodes(editor, { at: inlinePath.concat(n) });
          n--;
        }
      }
    }
  }
}

function validateInlineProps(editor: Editor, inlineEntry: NodeEntry<AlineInlineElement>): Node {
  const [inline, inlinePath] = inlineEntry;

  switch (inline.type) {
    case 'INPUT': {
      if (isCheckboxOptionFieldElement(inline)) {
        validateCheckboxOptionProps(editor, [inline, inlinePath]);
        return Node.get(editor, inlinePath);
      }
      validateInputFieldProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case 'link': {
      validateLinkProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case 'break': {
      validateBreakProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case bookmarkStartType:
    case bookmarkEndType:
    case suggestionMarkerStartType:
    case suggestionMarkerEndType:
    case cannedFieldType:
    case dynamicInlineType: {
      return inline;
    }
    case drawingType: {
      validateDrawingElementProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case instructionFieldElementType: {
      validateInlineInstructionFieldProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case tabType: {
      validateTabElementProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
    case checkboxType: {
      validateCheckboxElementProps(editor, [inline, inlinePath]);
      return Node.get(editor, inlinePath);
    }
  }
}

function validateLinkProps(editor: Editor, linkEntry: NodeEntry<LinkElement>): void {
  const [link, linkPath] = linkEntry;
  const { children, ...defaultLinkProps } = createDefaultLink('');
  const missingKeys = Object.keys(defaultLinkProps).filter(linkPropKey => {
    const hasKey = linkPropKey in link;
    return !hasKey;
  });
  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(editor, { suggestedEdits: [] }, { at: linkPath, voids: true });
        break;
      case 'type':
      case 'href':
        Transforms.unwrapNodes(editor, { at: linkPath, voids: true });
        break;
    }
  }
}

function validateBreakProps(editor: Editor, breakEntry: NodeEntry<BreakElement>): void {
  const [breakNode, breakNodePath] = breakEntry;
  const { children: breakChildren, ...breakProps } = breakNode;
  const { children: defaultBreakChildren, ...defaultBreakProps } = createDefaultBreak();
  const missingKeys = Object.keys(defaultBreakProps).filter(breakPropKey => {
    const hasKey = breakPropKey in breakNode;
    return !hasKey;
  });
  if (!missingKeys.length) return;

  const backFilledProps: Omit<BreakElement, 'children'> = mergeWithDefaults(
    breakProps,
    defaultBreakProps
  );
  Transforms.setNodes(editor, backFilledProps, { at: breakNodePath, voids: true });
}

function validateInputFieldProps(
  editor: Editor,
  inputFieldEntry: NodeEntry<InputFieldElement>
): void {
  const [inputField, inputFieldPath] = inputFieldEntry;
  const { children, ...defaultFieldProps } = createDefaultNonSigField();

  const missingKeys = Object.keys(defaultFieldProps).filter(inputFieldPropKey => {
    const hasKey = inputFieldPropKey in inputField;
    return !hasKey;
  });
  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(editor, { suggestedEdits: [] }, { at: inputFieldPath, voids: true });
        break;
      case 'fieldId':
      case 'variant':
        /**
         * If the fieldId or variant is missing, there is really no way for us to know these values apart from hitting the server, which we should avoid here.
         * So, we simply unwrap the input field.  Return after since any additional missing keys don't matter as the input field is now unwrapped/gone.
         */
        Transforms.unwrapNodes(editor, { at: inputFieldPath, voids: true });
        return;
      case 'formats':
        Transforms.setNodes(
          editor,
          { formats: Marks.default },
          { at: inputFieldPath, voids: true }
        );
        break;
      case 'type':
        Transforms.setNodes(editor, { type: inputFieldType }, { at: inputFieldPath, voids: true });
        break;
    }
  }
}

function validateDrawingElementProps(
  editor: Editor,
  drawingEntry: NodeEntry<DrawingElement>
): void {
  const [drawing, drawingPath] = drawingEntry;
  const { children, ...defaultFieldProps } = createDefaultDrawing({
    drawingImgId: '',
  });
  const missingKeys = Object.keys(defaultFieldProps).filter(drawingPropKey => {
    const hasKey = drawingPropKey in drawing;
    return !hasKey;
  });

  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(editor, { suggestedEdits: [] }, { at: drawingPath, voids: true });
        break;
      case 'type':
        Transforms.setNodes(editor, { type: drawingType }, { at: drawingPath, voids: true });
        break;
    }
  }
}

function validateInlineInstructionFieldProps(
  editor: Editor,
  instructionFieldEntry: NodeEntry<InlineInstructionFieldElement>
): void {
  const [instructionField, instructionFieldPath] = instructionFieldEntry;
  const { children, ...defaultInstructionFieldProps } = createDefaultInlineInstructionField(
    instructionField.instruction
  );

  const missingKeys = Object.keys(defaultInstructionFieldProps).filter(key => {
    const hasKey = key in instructionField;
    return !hasKey;
  });

  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(
          editor,
          { suggestedEdits: [] },
          { at: instructionFieldPath, voids: true }
        );
        break;
      case 'instruction':
      case 'switches':
        // If the instruction or switches are missing, we can't recover, so unwrap the field
        Transforms.unwrapNodes(editor, { at: instructionFieldPath, voids: true });
        return;
      case 'type':
        Transforms.setNodes(
          editor,
          { type: instructionFieldElementType },
          { at: instructionFieldPath, voids: true }
        );
        break;
    }
  }
}

function validateCheckboxOptionProps(
  editor: Editor,
  optionEntry: NodeEntry<CheckboxOptionFieldElement>
): void {
  const [option, optionPath] = optionEntry;
  if (!option.fieldId || !option.id) {
    Transforms.unwrapNodes(editor, { at: optionPath, voids: true });
    return;
  }
  if (option.type !== inputFieldType) {
    Transforms.setNodes(editor, { type: inputFieldType }, { at: optionPath, voids: true });
  }
  if (option.variant !== GQL.FieldVariant.Checkbox) {
    Transforms.setNodes(
      editor,
      { variant: GQL.FieldVariant.Checkbox },
      { at: optionPath, voids: true }
    );
  }

  const defaultOptionProps = omit(
    ['children'],
    createCheckboxOptionFieldElement({ checkboxOptionProps: option })
  );
  const missingKeys = Object.keys(defaultOptionProps).filter(key => !(key in option));

  if (missingKeys.length) {
    for (const missingKey of missingKeys) {
      switch (missingKey) {
        case 'suggestedEdits':
          Transforms.setNodes(editor, { suggestedEdits: [] }, { at: optionPath, voids: true });
          break;
        case 'fieldId':
        case 'id':
          Transforms.unwrapNodes(editor, { at: optionPath, voids: true });
          return;
      }
    }
  }

  const updatedOption = Node.get(editor, optionPath);
  if (!isCheckboxOptionFieldElement(updatedOption)) return;

  if (!updatedOption.children.some(isCheckboxElement)) {
    Transforms.unwrapNodes(editor, { at: optionPath, voids: true });
  }
}

function validateCheckboxElementProps(
  editor: Editor,
  checkboxEntry: NodeEntry<CheckboxElement>
): void {
  const [checkbox, checkboxPath] = checkboxEntry;
  const { children, ...defaultCheckboxProps } = createCheckboxElement({
    checkboxProps: { optionId: checkbox.optionId },
  });
  const missingKeys = Object.keys(defaultCheckboxProps).filter(key => !(key in checkbox));

  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(editor, { suggestedEdits: [] }, { at: checkboxPath, voids: true });
        break;
      case 'type':
        Transforms.setNodes(editor, { type: checkboxType }, { at: checkboxPath, voids: true });
        break;
    }
  }
}

function validateTabElementProps(editor: Editor, tabEntry: NodeEntry<TabElement>): void {
  const [tab, tabPath] = tabEntry;
  const { children, ...defaultTabProps } = {
    children: [],
    suggestedEdits: [],
    type: tabType,
  } satisfies TabElement;
  const missingKeys = Object.keys(defaultTabProps).filter(key => {
    const hasKey = key in tab;
    return !hasKey;
  });

  if (!missingKeys.length) return;

  for (const missingKey of missingKeys) {
    switch (missingKey) {
      case 'suggestedEdits':
        Transforms.setNodes(editor, { suggestedEdits: [] }, { at: tabPath, voids: true });
        break;
      case 'type':
        Transforms.setNodes(editor, { type: tabType }, { at: tabPath, voids: true });
        break;
    }
  }
}
